/**
 * Going somewhere else in /v2 from code rather than from a link.
 *
 * ── Why this is not just `location.assign` ────────────────────────────────
 *
 * `main.js` intercepts clicks on `<a href>` and swaps the view without
 * reloading, which is what keeps Firebase, the auth session and the Firestore
 * cache warm between screens. A handler that calls `location.assign` instead
 * throws all three away and pays for the bundle, the SDK and the auth
 * round-trip again — the full page load the router was built to end.
 *
 * Most navigation in /v2 IS a link and needs nothing from this file. What
 * needs it is the navigation that follows a decision: open the trip the
 * advisor just picked out of a grid of buttons, land on the trip that was
 * just duplicated. Those cannot be anchors, because the thing they sit inside
 * is already a button.
 *
 * So `main.js` registers its router here at boot and features call `goTo`.
 * Nothing else imports `main.js` — a feature importing the entry point would
 * be a cycle, and would drag the whole app into any test that touched it.
 *
 * ── The fallback is a real navigation, on purpose ─────────────────────────
 *
 * Before boot registers anything, and in any test that mounts a feature on
 * its own, `goTo` falls back to `location.assign`. That is the behaviour every
 * one of these call sites had before the router existed: slower, and correct.
 * A no-op would be worse than slow — it would be a button that does nothing.
 */

import { appUrl } from '../lib/app-base.js'
import { siteUrl } from './app-base.js'

/** @type {((href: string) => void)|null} */
let router = null

/**
 * Hand the router to everything that navigates. Called once, from `main.js`.
 * @param {((href: string) => void)|null} fn
 */
export function setRouter(fn) {
  router = typeof fn === 'function' ? fn : null
}

/**
 * Go to an in-app URL: through the router when there is one, by loading the
 * page when there is not.
 *
 * @param {string} href
 */
export function goTo(href) {
  const url = String(href || '')
  if (!url) return
  if (router) {
    router(url)
    return
  }
  globalThis.location.assign(url)
}

/**
 * Where a trip lives in /v2.
 *
 * The id is in the query string rather than the path so it survives a reload
 * and can be pasted to another device — legacy holds it in the module-global
 * `currentTripId` (app.html:5721), which is why refreshing the editor drops
 * the advisor back on the dashboard.
 *
 * @param {string} tripId
 * @returns {string}
 */
export function tripUrl(tripId) {
  return appUrl(`/itinerary?trip=${encodeURIComponent(String(tripId || ''))}`)
}

/**
 * The bridge back to the legacy editor for ONE trip.
 *
 * Still needed: /v2 can open, list, re-status, duplicate and delete a trip,
 * but the editor itself is step 4.3. Every use of this is a place the advisor
 * still pays for a 590KB cold start, so the count of call sites is the honest
 * measure of how far 4.2/4.3 have got.
 *
 * @param {string} tripId
 * @returns {string}
 */
export function legacyTripUrl(tripId) {
  return siteUrl(`/app?openTrip=${encodeURIComponent(String(tripId || ''))}`)
}
